import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import Create from "./pages/Create";
import Reviews from "./pages/Reviews";
import GBP from "./pages/GBP";
import Onboarding from "./pages/Onboarding";
import Admin from "./pages/Admin";
import AdminVideo from "./pages/AdminVideo";
import AdminAnalytics from "./pages/AdminAnalytics";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import AuthCallback from "./pages/AuthCallback";
import MagicLinkPage from "./pages/MagicLinkPage";
import CustomerJourneys from "./pages/CustomerJourneys";
import ShareGuide from "./pages/ShareGuide";
import ChoosePlan from "./pages/ChoosePlan";
import BusinessProfilePage from "./pages/BusinessProfilePage";
import Contact from "./pages/Contact";
import LegalIndex from "./pages/legal/LegalIndex";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import TrialTerms from "./pages/legal/TrialTerms";
import ServiceAgreement from "./pages/legal/ServiceAgreement";
import CookiesPolicy from "./pages/legal/Cookies";
import AcceptableUse from "./pages/legal/AcceptableUse";
import Refund from "./pages/legal/Refund";
import DPA from "./pages/legal/DPA";
import ASA from "./pages/legal/ASA";
import ProtectedRoute from "./components/ProtectedRoute";
import SubscriptionGate from "./components/SubscriptionGate";
import WhatsAppFab from "./components/WhatsAppFab";
import useAnalytics from "./hooks/useAnalytics";

/** Hotjar — session-recording + heatmaps. Reads REACT_APP_HOTJAR_SITE_ID
 * from env; if not set, this is a no-op so dev builds don't ping Hotjar.
 * Founder action: sign up at hotjar.com → copy Site ID → set as env var →
 * redeploy. Free tier = 35 sessions/day, plenty for diagnosing the
 * email-capture drop-off on /. */
const initHotjar = (siteId) => {
  if (!siteId || typeof window === 'undefined' || window.hj) return;
  (function (h, o, t, j, a, r) {
    h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments); };
    h._hjSettings = { hjid: Number(siteId), hjsv: 6 };
    a = o.getElementsByTagName('head')[0];
    r = o.createElement('script');
    r.async = 1;
    r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
    a.appendChild(r);
  })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
};

/** Inner shell — needs to be a child of <BrowserRouter> so router hooks
 * (useLocation, useAnalytics) resolve correctly. */
const AppShell = () => {
  // Auto-tracks page_view on every route change (for the founder dashboard)
  useAnalytics();
  // Boot Hotjar once on first mount (no-op if env var not set)
  useEffect(() => { initHotjar(process.env.REACT_APP_HOTJAR_SITE_ID); }, []);
  const location = useLocation();
  // Hide the floating WhatsApp button inside the admin areas — too noisy
  const isAdmin = location.pathname.startsWith('/admin');
  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/for/:industry" element={<Landing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/magic" element={<MagicLinkPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><SubscriptionGate><Dashboard /></SubscriptionGate></ProtectedRoute>} />
        <Route path="/create" element={<ProtectedRoute><SubscriptionGate><Create /></SubscriptionGate></ProtectedRoute>} />
        <Route path="/reviews" element={<ProtectedRoute><SubscriptionGate><Reviews /></SubscriptionGate></ProtectedRoute>} />
        <Route path="/gbp" element={<ProtectedRoute><SubscriptionGate><GBP /></SubscriptionGate></ProtectedRoute>} />
        <Route path="/share-guide" element={<ProtectedRoute><SubscriptionGate><ShareGuide /></SubscriptionGate></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/onboarding/business" element={<ProtectedRoute><SubscriptionGate><BusinessProfilePage onboarding /></SubscriptionGate></ProtectedRoute>} />
        <Route path="/settings/businesses" element={<ProtectedRoute><SubscriptionGate><BusinessProfilePage /></SubscriptionGate></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/admin/video" element={<ProtectedRoute><AdminVideo /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
        <Route path="/admin/journeys" element={<ProtectedRoute><CustomerJourneys /></ProtectedRoute>} />
        <Route path="/choose-plan" element={<ProtectedRoute><ChoosePlan /></ProtectedRoute>} />
        <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
        <Route path="/billing/cancel" element={<BillingCancel />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* Legal — public */}
        <Route path="/legal" element={<LegalIndex />} />
        <Route path="/legal/terms" element={<Terms />} />
        <Route path="/legal/privacy" element={<Privacy />} />
        <Route path="/legal/trial-terms" element={<TrialTerms />} />
        <Route path="/legal/service-agreement" element={<ServiceAgreement />} />
        <Route path="/legal/cookies" element={<CookiesPolicy />} />
        <Route path="/legal/acceptable-use" element={<AcceptableUse />} />
        <Route path="/legal/refund" element={<Refund />} />
        <Route path="/legal/dpa" element={<DPA />} />
        <Route path="/legal/asa" element={<ASA />} />
      </Routes>
      {!isAdmin && <WhatsAppFab />}
    </>
  );
};

function App() {
  useEffect(() => {
    // Remove any injected third-party branding / trackers on every route change
    const cleanup = () => {
      try {
        const badge = document.getElementById('emergent-badge');
        if (badge) badge.remove();
        // Also nuke any future ones via MutationObserver (in case injected late)
      } catch (e) { /* noop */ }
      try {
        if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
          window.posthog.opt_out_capturing();
        }
        if (window.posthog && typeof window.posthog.stopSessionRecording === 'function') {
          window.posthog.stopSessionRecording();
        }
      } catch (e) { /* noop */ }
    };
    cleanup();
    const interval = setInterval(cleanup, 500);
    // Stop polling after 10s (badge usually injected in first few seconds)
    setTimeout(() => clearInterval(interval), 10000);

    // Observer for late-injected badge
    const observer = new MutationObserver(cleanup);
    observer.observe(document.body, { childList: true, subtree: false });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
